import { CHAPTER_CACHE_MAX_SIZE } from '@/constants/reader';

import type { IReaderBookData } from '../types';

/**
 * 章节数据 LRU 内存缓存。
 *
 * 与 chapterRenderList 的关系：
 * - chapterRenderList 是「当前渲染在 DOM 中的章节」，容量小（6）
 * - chapterCache 是「曾经加载过的章节」，容量大（20）
 * - 章节从渲染区回收时，数据仍保留在 Cache 中，再次需要时免请求
 *
 * 基于 Map 的插入顺序实现 LRU：每次 get/set 时把 key 移到最新。
 */
class ChapterCache {
    private readonly cache = new Map<string, IReaderBookData>();

    get(chapterId: string): IReaderBookData | undefined {
        const data = this.cache.get(chapterId);
        if (data) {
            // 刷新为最近使用
            this.cache.delete(chapterId);
            this.cache.set(chapterId, data);
        }
        return data;
    }

    set(chapterId: string, data: IReaderBookData): void {
        if (this.cache.has(chapterId)) {
            this.cache.delete(chapterId);
        }
        this.cache.set(chapterId, data);
        while (this.cache.size > CHAPTER_CACHE_MAX_SIZE) {
            const oldestKey = this.cache.keys().next().value;
            if (oldestKey === undefined) {
                break;
            }
            this.cache.delete(oldestKey);
        }
    }

    has(chapterId: string): boolean {
        return this.cache.has(chapterId);
    }

    clear(): void {
        this.cache.clear();
    }
}

export const chapterCache = new ChapterCache();
export const endRecommendChapterCache = new ChapterCache();
